import axios from 'axios';

export class MarketExplorerHttpError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = 'MarketExplorerHttpError';
    this.status = options.status || null;
    this.fields = options.fields || {};
    this.canceled = Boolean(options.canceled);
    this.cause = options.cause;
  }
}

export function createMarketExplorerClient(options = {}) {
  const transport = options.transport || axios.create({
    baseURL: process.env.VUE_APP_API || '',
    timeout: 60000,
    withCredentials: true,
    headers: { 'X-Requested-With': 'XMLHttpRequest' }
  });

  if (transport.interceptors && transport.interceptors.request) {
    transport.interceptors.request.use(config => {
      const token = localStorage.getItem('user_access_token');
      if (token) {
        config.headers = config.headers || {};
        config.headers.authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  function wrap(error) {
    if (axios.isCancel(error) || (error && error.canceled)) {
      return new MarketExplorerHttpError('Solicitud cancelada.', { canceled: true, cause: error });
    }
    const response = error && error.response;
    const apiError = response && response.data && response.data.error;
    return new MarketExplorerHttpError(
      (apiError && apiError.message) || (error && error.message) || 'No se pudo consultar el mercado.',
      { status: response ? response.status : null, fields: (apiError && apiError.fields) || {}, cause: error }
    );
  }

  async function request(method, url, data, config = {}) {
    try {
      const response = await transport.request({
        method, url, data, ...config,
        headers: { 'Content-Type': 'application/json', ...(config.headers || {}) }
      });
      return response.data;
    } catch (error) {
      throw wrap(error);
    }
  }

  return {
    createCancelSource: () => axios.CancelToken.source(),
    execute: (definition, config) => request('post', '/api/v1/stock-finder/query', definition, config),
    getLast: async config => {
      const response = await request('get', '/api/v1/stock-finder/query-definitions/last', undefined, config);
      return response.data;
    },
    saveLast: definition => request('put', '/api/v1/stock-finder/query-definitions/last', definition),
    deleteLast: () => request('delete', '/api/v1/stock-finder/query-definitions/last'),
    listSaved: async () => {
      const response = await request('get', '/api/v1/stock-finder/query-definitions/saved');
      return response.data;
    },
    getSaved: async name => {
      const response = await request('get', `/api/v1/stock-finder/query-definitions/saved/${encodeURIComponent(name)}`);
      return response.data;
    },
    saveNamed: (name, definition) => request('post', '/api/v1/stock-finder/query-definitions/saved', { name, definition }),
    updateNamed: (name, input = {}) => request(
      'put',
      `/api/v1/stock-finder/query-definitions/saved/${encodeURIComponent(name)}`,
      {
        ...(input.newName ? { new_name: input.newName } : {}),
        ...(input.definition ? { definition: input.definition } : {})
      }
    ),
    deleteNamed: name => request('delete', `/api/v1/stock-finder/query-definitions/saved/${encodeURIComponent(name)}`)
  };
}

export const marketExplorerClient = createMarketExplorerClient();
